import {
  AuditOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  PlusOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  SnippetsOutlined,
  SwapOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  message,
  Modal,
  Progress,
  Select,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { ProjectPhase } from "../../projectPhase";
import { getTaskSubtaskContentAssignees } from "../../taskSubtaskContentAssignees";
import SubtaskContentChecklistModal, {
  SubtaskContentItem,
} from "../SubtaskContentChecklistModal";
import TaskPriorityTag, { TaskPriority } from "../TaskPriorityTag";
import {
  copyTaskTemplate,
  readTaskTemplate,
  TaskContentClipboardTemplate,
  TaskSubtaskClipboardTemplate,
} from "../taskTemplateClipboard";
import "./index.css";

export type TaskSubtaskStatus =
  | "todo"
  | "transfer"
  | "review"
  | "rejected"
  | "done";

export interface TaskSubtaskItem {
  id: string;
  parent_task_id: string;
  project_id: string;
  phase: ProjectPhase;
  creator: string;
  title: string;
  transferees?: string[];
  reviewers: string[];
  content_items: SubtaskContentItem[];
  status: TaskSubtaskStatus;
  rejection_reason?: string;
  transferred_at?: number;
  submitted_at?: number;
  reviewed_at?: number;
  revision: number;
  created_at: number;
  updated_at: number;
}

export interface TaskSubtaskDraft {
  title: string;
  transferees: string[];
  reviewers: string[];
  content_items: SubtaskContentItem[];
}

export interface TaskSubtaskMemberOption {
  label: string;
  value: string;
  isProjectMember?: boolean;
}

export interface TaskSubtaskManagerProps {
  subtasks: TaskSubtaskItem[];
  loading: boolean;
  canManage: boolean;
  canManageContentStatus?: boolean;
  layoutMode?: "editor" | "detail";
  currentUserId: string;
  focusedContentItemId?: string;
  focusedSubtaskId?: string;
  selectedSubtaskId?: string;
  memberOptions: TaskSubtaskMemberOption[];
  getMemberName: (userId: string) => string;
  defaultAssignee?: string;
  onCreate: (draft: TaskSubtaskDraft) => Promise<boolean>;
  onUpdate: (subtask: TaskSubtaskItem, draft: TaskSubtaskDraft) => Promise<boolean>;
  onDelete: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onHandoff?: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onSubmitReview: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onComplete?: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onApprove: (subtask: TaskSubtaskItem) => Promise<boolean>;
  onReject: (subtask: TaskSubtaskItem, reason: string) => Promise<boolean>;
  onContentClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onEditingChange?: (editing: boolean) => void;
  onSelectedSubtaskChange?: (subtaskId: string) => void;
}

const STATUS_META: Record<
  TaskSubtaskStatus,
  { label: string; className: string }
> = {
  todo: { label: "待完成", className: "is-todo" },
  transfer: { label: "待转交", className: "is-transfer" },
  review: { label: "待审批", className: "is-review" },
  rejected: { label: "已驳回", className: "is-rejected" },
  done: { label: "已完成", className: "is-done" },
};

function getGroupedMemberSelectOptions(options: TaskSubtaskMemberOption[]) {
  const toSelectOption = (option: TaskSubtaskMemberOption) => ({
    value: option.value,
    selectedLabel: option.label,
    searchText: `${option.label} ${option.value}`,
    label: (
      <span className="taskSubtaskMemberOption">
        <i className={option.isProjectMember === false ? "is-team" : "is-project"}>
          <UserOutlined />
        </i>
        <span className="taskSubtaskMemberOption__identity">
          <strong>{option.label}</strong>
          <small>账号：{option.value}</small>
        </span>
        <em className={option.isProjectMember === false ? "is-team" : "is-project"}>
          {option.isProjectMember === false ? "选择后自动加入" : "项目内"}
        </em>
      </span>
    ),
  });
  const projectMembers = options.filter(
    (option) => option.isProjectMember !== false
  );
  const otherTeamMembers = options.filter(
    (option) => option.isProjectMember === false
  );
  return [
    ...(projectMembers.length
      ? [
          {
            label: "项目成员 · 优先显示",
            options: projectMembers.map(toSelectOption),
          },
        ]
      : []),
    ...(otherTeamMembers.length
      ? [
          {
            label: "其他团队成员 · 选择后自动加入项目",
            options: otherTeamMembers.map(toSelectOption),
          },
        ]
      : []),
  ];
}

function getPlainText(html: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html || "";
  return (wrapper.textContent || "").replace(/\s+/g, " ").trim();
}

function getContentItemLabel(content: string) {
  return getPlainText(content) || (/<img\b/i.test(content) ? "图片内容" : "未填写内容");
}

function getContentProgress(items: SubtaskContentItem[]) {
  const completed = items.filter((item) => item.completed).length;
  return {
    completed,
    total: items.length,
    progress: items.length ? Math.round((completed / items.length) * 100) : 0,
  };
}

function isContentItemOverdue(item: SubtaskContentItem) {
  return !item.completed && item.due_time < Date.now();
}

function getContentItemOverdueDays(item: SubtaskContentItem) {
  return Math.max(1, Math.ceil((Date.now() - item.due_time) / 86400000));
}

function getContentItemDueText(item: SubtaskContentItem) {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(item.due_time));
}

function getContentItemStartText(item: SubtaskContentItem) {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(item.start_time));
}

function getSubtaskDueTime(items: SubtaskContentItem[]) {
  return Math.max(
    0,
    ...items.map((item) => Number(item.due_time) || 0).filter(Boolean)
  );
}

function getSubtaskStartTime(items: SubtaskContentItem[]) {
  const values = items
    .map((item) => Number(item.start_time) || 0)
    .filter((value) => value > 0);
  return values.length ? Math.min(...values) : 0;
}

function getSubtaskPriority(items: SubtaskContentItem[]): TaskPriority {
  const rank: Record<TaskPriority, number> = {
    low: 0,
    medium: 1,
    high: 2,
    urgent: 3,
  };
  return items.reduce<TaskPriority>(
    (highest, item) =>
      rank[item.priority] > rank[highest] ? item.priority : highest,
    "low"
  );
}

function getSubtaskDateText(value: number) {
  return value
    ? new Intl.DateTimeFormat("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(value))
    : "未设置";
}

function createDraftContentItem(
  item: TaskContentClipboardTemplate,
  index: number
): SubtaskContentItem {
  const now = Date.now();
  return {
    item_id: `draft-content-${now}-${index}`,
    content: item.content,
    completed: false,
    start_time: item.start_time,
    due_time: item.due_time,
    priority: item.priority,
    assignees: [...item.assignees],
    created_at: now,
    updated_at: now,
  };
}

const TaskSubtaskManager: FC<TaskSubtaskManagerProps> = ({
  subtasks,
  loading,
  canManage,
  canManageContentStatus = false,
  layoutMode = "editor",
  currentUserId,
  focusedContentItemId,
  focusedSubtaskId,
  selectedSubtaskId: controlledSelectedSubtaskId,
  memberOptions,
  getMemberName,
  defaultAssignee,
  onCreate,
  onUpdate,
  onDelete,
  onSubmitReview,
  onComplete = async () => false,
  onApprove,
  onReject,
  onContentClick,
  onEditingChange,
  onSelectedSubtaskChange,
}) => {
  const [editing, setEditing] = useState<TaskSubtaskItem | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [transferees, setTransferees] = useState<string[]>([]);
  const [reviewers, setReviewers] = useState<string[]>([]);
  const [contentItems, setContentItems] = useState<SubtaskContentItem[]>([]);
  const [contentChecklistOpen, setContentChecklistOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workflowBusyId, setWorkflowBusyId] = useState("");
  const [deleting, setDeleting] = useState<TaskSubtaskItem | null>(null);
  const [rejecting, setRejecting] = useState<TaskSubtaskItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [internalSelectedSubtaskId, setInternalSelectedSubtaskId] = useState(
    focusedSubtaskId || ""
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const focusedContentItemRef = useRef<HTMLElement | null>(null);
  const focusedSubtaskRef = useRef<HTMLElement | null>(null);
  const editorInitialDraftRef = useRef<TaskSubtaskDraft | null>(null);
  const detailMode = layoutMode === "detail";
  const completedCount = subtasks.filter((item) => item.status === "done").length;
  const progress = subtasks.length
    ? Math.round((completedCount / subtasks.length) * 100)
    : 0;
  const orderedSubtasks = useMemo(
    () =>
      [...subtasks].sort((left, right) => {
        const focusDifference =
          Number(right.id === focusedSubtaskId) -
          Number(left.id === focusedSubtaskId);
        if (focusDifference) return focusDifference;
        const transferDifference =
          Number(
            (right.status === "transfer" || right.status === "rejected") &&
              (right.transferees || []).includes(currentUserId)
          ) -
          Number(
            (left.status === "transfer" || left.status === "rejected") &&
              (left.transferees || []).includes(currentUserId)
          );
        if (transferDifference) return transferDifference;
        const reviewDifference =
          Number(
            right.status === "review" &&
              right.reviewers.includes(currentUserId)
          ) -
          Number(
            left.status === "review" &&
              left.reviewers.includes(currentUserId)
          );
        return reviewDifference || left.created_at - right.created_at;
      }),
    [currentUserId, focusedSubtaskId, subtasks]
  );
  const contentAssignees = useMemo(
    () => getTaskSubtaskContentAssignees({ content_items: contentItems }),
    [contentItems]
  );
  const reviewerOptions = useMemo(
    () =>
      memberOptions.filter(
        (option) =>
          !contentAssignees.includes(option.value) &&
          !transferees.includes(option.value)
      ),
    [contentAssignees, memberOptions, transferees]
  );
  const transfereeOptions = useMemo(
    () =>
      memberOptions.filter(
        (option) =>
          !contentAssignees.includes(option.value) &&
          !reviewers.includes(option.value)
      ),
    [contentAssignees, memberOptions, reviewers]
  );
  const transfereeSelectOptions = useMemo(
    () => getGroupedMemberSelectOptions(transfereeOptions),
    [transfereeOptions]
  );
  const reviewerSelectOptions = useMemo(
    () => getGroupedMemberSelectOptions(reviewerOptions),
    [reviewerOptions]
  );
  const selectedSubtaskId =
    controlledSelectedSubtaskId ?? internalSelectedSubtaskId;
  const selectedSubtask =
    orderedSubtasks.find((subtask) => subtask.id === selectedSubtaskId) || null;

  useEffect(() => {
    onEditingChange?.(Boolean(editing) || saving || Boolean(workflowBusyId));
  }, [editing, onEditingChange, saving, workflowBusyId]);

  useEffect(() => {
    if (
      editing &&
      editing !== "new" &&
      !subtasks.some((subtask) => subtask.id === editing.id)
    ) {
      setEditing(null);
      message.warning("正在编辑的子任务已被其他成员删除");
    }
  }, [editing, subtasks]);

  useEffect(() => {
    if (!focusedSubtaskId || loading) return;
    const frame = window.requestAnimationFrame(() => {
      focusedSubtaskRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusedSubtaskId, loading, orderedSubtasks]);

  useEffect(() => {
    if (!focusedContentItemId || loading || !selectedSubtask) return;
    focusedContentItemRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [focusedContentItemId, loading, selectedSubtask]);

  useEffect(() => {
    if (!detailMode || loading) return;
    if (
      controlledSelectedSubtaskId === undefined &&
      focusedSubtaskId &&
      orderedSubtasks.some((subtask) => subtask.id === focusedSubtaskId)
    ) {
      setInternalSelectedSubtaskId(focusedSubtaskId);
      return;
    }
    if (
      selectedSubtaskId &&
      !orderedSubtasks.some((subtask) => subtask.id === selectedSubtaskId)
    ) {
      if (controlledSelectedSubtaskId === undefined) {
        setInternalSelectedSubtaskId("");
      }
      onSelectedSubtaskChange?.("");
    }
  }, [
    controlledSelectedSubtaskId,
    detailMode,
    focusedSubtaskId,
    loading,
    onSelectedSubtaskChange,
    orderedSubtasks,
    selectedSubtaskId,
  ]);

  const beginCreate = () => {
    editorInitialDraftRef.current = {
      title: "",
      transferees: [],
      reviewers: [],
      content_items: [],
    };
    setTitle("");
    setTransferees([]);
    setReviewers([]);
    setContentItems([]);
    setEditing("new");
  };

  const copySubtask = async (subtask: TaskSubtaskItem) => {
    await copyTaskTemplate<TaskSubtaskClipboardTemplate>("subtask", {
      title: subtask.title,
      transferees: [...(subtask.transferees || [])],
      reviewers: [...subtask.reviewers],
      content_items: subtask.content_items.map((item) => ({
        content: item.content,
        start_time: item.start_time,
        due_time: item.due_time,
        priority: item.priority,
        assignees: [...item.assignees],
      })),
    });
    message.success("子任务已复制，可在新建子任务中粘贴");
  };

  const pasteSubtask = async () => {
    const template = await readTaskTemplate<TaskSubtaskClipboardTemplate>(
      "subtask"
    );
    if (!template) {
      message.warning("剪贴板中没有可用的子任务数据");
      return;
    }
    const availableMembers = new Set(memberOptions.map((option) => option.value));
    const fallbackAssignee = availableMembers.has(defaultAssignee || "")
      ? defaultAssignee || ""
      : memberOptions[0]?.value || "";
    const nextContentItems = (template.content_items || []).map((item, index) =>
      createDraftContentItem(
        {
          ...item,
          assignees: (item.assignees || []).filter((id) => availableMembers.has(id))
            .length
            ? (item.assignees || []).filter((id) => availableMembers.has(id))
            : fallbackAssignee
            ? [fallbackAssignee]
            : [],
        },
        index
      )
    );
    const nextContentAssignees = getTaskSubtaskContentAssignees({
      content_items: nextContentItems,
    });
    const nextTransferees = (template.transferees || []).filter(
      (id) => availableMembers.has(id) && !nextContentAssignees.includes(id)
    );
    const nextReviewers = (template.reviewers || []).filter(
      (id) =>
        availableMembers.has(id) &&
        !nextContentAssignees.includes(id) &&
        !nextTransferees.includes(id)
    );
    const nextDraft: TaskSubtaskDraft = {
      title: template.title || "",
      transferees: nextTransferees,
      reviewers: nextReviewers,
      content_items: nextContentItems,
    };
    setTitle(nextDraft.title);
    setTransferees(nextDraft.transferees);
    setReviewers(nextDraft.reviewers);
    setContentItems(nextDraft.content_items);
    message.success(
      "已粘贴子任务数据，流程状态已重置；不可用成员已自动过滤"
    );
  };

  const beginEdit = (subtask: TaskSubtaskItem) => {
    editorInitialDraftRef.current = {
      title: subtask.title,
      transferees: [...(subtask.transferees || [])],
      reviewers: [...subtask.reviewers],
      content_items: subtask.content_items.map((item) => ({ ...item })),
    };
    setTitle(subtask.title);
    setTransferees([...(subtask.transferees || [])]);
    setReviewers([...subtask.reviewers]);
    setContentItems(subtask.content_items.map((item) => ({ ...item })));
    setEditing(subtask);
  };

  const resetEditor = () => {
    setEditing(null);
    setTitle("");
    setTransferees([]);
    setReviewers([]);
    setContentItems([]);
    setContentChecklistOpen(false);
    editorInitialDraftRef.current = null;
  };

  const hasUnsavedEditorChanges = () => {
    const initialDraft = editorInitialDraftRef.current;
    if (!editing || !initialDraft) return false;
    return (
      title !== initialDraft.title ||
      JSON.stringify(transferees) !==
        JSON.stringify(initialDraft.transferees) ||
      JSON.stringify(reviewers) !== JSON.stringify(initialDraft.reviewers) ||
      JSON.stringify(contentItems) !==
        JSON.stringify(initialDraft.content_items)
    );
  };

  const switchEditorToSubtask = (subtask: TaskSubtaskItem) => {
    if (editing !== "new" && editing?.id === subtask.id) return;
    if (!editing || !hasUnsavedEditorChanges()) {
      beginEdit(subtask);
      return;
    }
    Modal.confirm({
      maskClosable: false,
      keyboard: false,
      centered: true,
      className: "taskSubtaskSwitchConfirm",
      icon: <ExclamationCircleOutlined />,
      title: "切换编辑的子任务？",
      content: "当前子任务有未保存的修改，切换后这些修改将丢失。",
      okText: "放弃修改并切换",
      cancelText: "继续编辑",
      onOk: () => beginEdit(subtask),
    });
  };

  const save = async () => {
    if (!title.trim()) {
      message.warning("请输入子任务标题");
      return;
    }
    if (!contentItems.length) {
      message.warning("请至少新增一项任务内容");
      return;
    }
    if (contentItems.some((item) => !item.assignees.length)) {
      message.warning("每项任务内容都必须至少选择一名负责人");
      return;
    }
    if (
      transferees.some((transferee) => contentAssignees.includes(transferee)) ||
      reviewers.some(
        (reviewer) =>
          contentAssignees.includes(reviewer) || transferees.includes(reviewer)
      )
    ) {
      message.warning("任务内容负责人、转让人和审核人不能重复");
      return;
    }
    setSaving(true);
    try {
      const draft = {
        title: title.trim(),
        transferees,
        reviewers,
        content_items: contentItems,
      };
      const succeeded =
        editing === "new"
          ? await onCreate(draft)
          : editing
          ? await onUpdate(editing, draft)
          : false;
      if (succeeded) resetEditor();
    } finally {
      setSaving(false);
    }
  };

  const runWorkflowAction = async (
    subtask: TaskSubtaskItem,
    action: () => Promise<boolean>
  ) => {
    setWorkflowBusyId(subtask.id);
    try {
      return await action();
    } finally {
      setWorkflowBusyId("");
    }
  };

  const togglePersistedContentItem = async (
    subtask: TaskSubtaskItem,
    itemId: string
  ) => {
    const targetItem = subtask.content_items.find((item) => item.item_id === itemId);
    if (
      !canManageContentStatus &&
      !targetItem?.assignees.includes(currentUserId)
    ) {
      message.warning("只有该内容项负责人或管理员可以修改完成状态");
      return;
    }
    const nextItems = subtask.content_items.map((item) =>
      item.item_id === itemId
        ? { ...item, completed: !item.completed, updated_at: Date.now() }
        : item
    );
    await runWorkflowAction(subtask, () =>
      onUpdate(subtask, {
        title: subtask.title,
        transferees: subtask.transferees || [],
        reviewers: subtask.reviewers,
        content_items: nextItems,
      })
    );
  };

  const closeRejectionModal = () => {
    if (workflowBusyId) return;
    setRejecting(null);
    setRejectionReason("");
  };

  const confirmRejection = async () => {
    if (!rejecting || !rejectionReason.trim()) return;
    const succeeded = await runWorkflowAction(rejecting, () =>
      onReject(rejecting, rejectionReason.trim())
    );
    if (succeeded) {
      setRejecting(null);
      setRejectionReason("");
    }
  };

  const selectSubtask = (subtask: TaskSubtaskItem) => {
    const nextSelectedId = selectedSubtaskId === subtask.id ? "" : subtask.id;
    if (controlledSelectedSubtaskId === undefined) {
      setInternalSelectedSubtaskId(nextSelectedId);
    }
    onSelectedSubtaskChange?.(nextSelectedId);
    if (detailMode && window.innerWidth <= 900) {
      setDetailModalOpen(Boolean(nextSelectedId));
    }
  };

  const handleSubtaskCardClick = (
    event: MouseEvent<HTMLElement>,
    subtask: TaskSubtaskItem,
    isBusy: boolean
  ) => {
    const target = event.target as HTMLElement;
    const interactiveTarget = target.closest(
      "button, a, input, textarea, select, [role='button'], [contenteditable='true']"
    );
    if (interactiveTarget && interactiveTarget !== event.currentTarget) {
      return;
    }
    if (detailMode) {
      selectSubtask(subtask);
      return;
    }
    if (canManage && !saving && !isBusy) {
      switchEditorToSubtask(subtask);
    }
  };

  const renderWorkflowActions = (subtask: TaskSubtaskItem) => {
    const isBusy = workflowBusyId === subtask.id;
    const subtaskTransferees = subtask.transferees || [];
    const contentReady =
      subtask.content_items.length > 0 &&
      subtask.content_items.every((item) => item.completed);
    const subtaskAssignees = getTaskSubtaskContentAssignees(subtask);
    const isAssignee = subtaskAssignees.includes(currentUserId);
    const isTransferee = subtaskTransferees.includes(currentUserId);
    const isReviewer = subtask.reviewers.includes(currentUserId);
    const hasTransferStep = subtaskTransferees.length > 0;
    const isTransferResubmission =
      hasTransferStep && subtask.status === "rejected";
    const canSubmit = subtask.revision > 0 &&
      (hasTransferStep
        ? isTransferee &&
          (subtask.status === "transfer" || isTransferResubmission)
        : isAssignee && subtask.status === "rejected");
    const canReview =
      subtask.revision > 0 && isReviewer && subtask.status === "review";
    const canComplete =
      subtask.revision > 0 &&
      !subtask.reviewers.length &&
      (hasTransferStep
        ? isTransferee && subtask.status === "transfer"
        : false);
    const actionIsBlocked =
      saving ||
      isBusy ||
      editing === "new" ||
      Boolean(editing && hasUnsavedEditorChanges());
    const getActionHint = (
      action: "submit" | "complete" | "review"
    ) => {
      if (actionIsBlocked) return "请先保存或取消当前修改，或等待操作结束";
      if (action === "review" && !isReviewer) return "仅指定审核人可以审批";
      if (
        (action === "submit" || action === "complete") &&
        hasTransferStep &&
        !isTransferee
      ) {
        return "仅指定转让人可以操作";
      }
      if (
        (action === "submit" || action === "complete") &&
        !hasTransferStep &&
        !isAssignee
      ) {
        return "仅任务内容负责人可以操作";
      }
      if (!contentReady) {
        return `请先完成全部任务内容后再${
          action === "submit"
            ? "提交审批"
            : "标记完成"
        }`;
      }
      return "";
    };

    return (
      <>
        {subtask.revision > 0 && subtask.status === "todo" && !contentReady && (
          <div className="taskSubtaskManager__workflowHint">
            <ExclamationCircleOutlined />
            完成全部任务内容后才可
            自动流转到下一环节
          </div>
        )}
        {subtask.revision > 0 &&
          subtask.status === "rejected" &&
          !hasTransferStep &&
          subtask.reviewers.length > 0 && (
            <div className="taskSubtaskManager__workflowActions">
              <Button
                aria-label={`提交审批 ${subtask.title}`}
                disabled={!canSubmit || actionIsBlocked || !contentReady}
                icon={<SendOutlined />}
                loading={isBusy}
                onClick={() =>
                  runWorkflowAction(subtask, () => onSubmitReview(subtask))
                }
                type="primary"
              >
                提交审批
              </Button>
              {!canSubmit || actionIsBlocked || !contentReady ? (
                <span className="taskSubtaskManager__workflowDisabledHint">
                  {getActionHint("submit")}
                </span>
              ) : null}
            </div>
          )}
        {subtask.revision > 0 &&
          (subtask.status === "transfer" || isTransferResubmission) && (
          <div className="taskSubtaskManager__reviewDecision is-transfer-step">
            <div className="taskSubtaskManager__reviewDecisionLabel">
              <SwapOutlined />
              <span>
                <strong>
                  {isTransferee
                    ? isTransferResubmission
                      ? "审批已驳回，等待你重新提交"
                      : "等待你确认并继续流转"
                    : isTransferResubmission
                    ? "审批已驳回，等待指定转让人重新提交"
                    : "等待指定转让人处理"}
                </strong>
                <small>
                  {isTransferResubmission
                    ? "确认修改内容后，再次提交给审核人"
                    : subtask.reviewers.length
                    ? "确认负责人交付内容后，提交给审核人"
                    : "确认负责人交付内容后，直接完成子任务"}
                </small>
              </span>
            </div>
            <div className="taskSubtaskManager__workflowActions">
              <Button
                aria-label={`${
                  subtask.reviewers.length
                    ? isTransferResubmission
                      ? "重新提交审批"
                      : "提交审批"
                    : "确认完成"
                } ${subtask.title}`}
                className={subtask.reviewers.length ? "is-handoff" : "is-approve"}
                disabled={
                  !(subtask.reviewers.length ? canSubmit : canComplete) ||
                  actionIsBlocked
                }
                icon={subtask.reviewers.length ? <SendOutlined /> : <CheckOutlined />}
                loading={isBusy}
                onClick={() =>
                  runWorkflowAction(subtask, () =>
                    subtask.reviewers.length
                      ? onSubmitReview(subtask)
                      : onComplete(subtask)
                  )
                }
                type="primary"
              >
                {subtask.reviewers.length
                  ? isTransferResubmission
                    ? "重新提交审批"
                    : "提交审批"
                  : "确认完成"}
              </Button>
              {!(subtask.reviewers.length ? canSubmit : canComplete) ||
              actionIsBlocked ? (
                <span className="taskSubtaskManager__workflowDisabledHint">
                  {getActionHint(
                    subtask.reviewers.length ? "submit" : "complete"
                  )}
                </span>
              ) : null}
            </div>
          </div>
        )}
        {subtask.revision > 0 && subtask.status === "review" && (
          <div className="taskSubtaskManager__reviewDecision">
            <div className="taskSubtaskManager__reviewDecisionLabel">
              <AuditOutlined />
              <span>
                <strong>{canReview ? "等待你的审核决定" : "等待指定审核人处理"}</strong>
                <small>
                  {canReview
                    ? "确认成果，或给出明确的修改意见"
                    : "仅指定审核人可以通过或退回修改"}
                </small>
              </span>
            </div>
            <div className="taskSubtaskManager__workflowActions">
              <Button
                aria-label={`通过子任务 ${subtask.title}`}
                className="is-approve"
                disabled={!canReview || actionIsBlocked}
                icon={<CheckOutlined />}
                loading={isBusy}
                onClick={() =>
                  runWorkflowAction(subtask, () => onApprove(subtask))
                }
                type="primary"
              >
                审核通过
              </Button>
              <Button
                aria-label={`驳回子任务 ${subtask.title}`}
                className="is-reject"
                danger
                disabled={!canReview || actionIsBlocked}
                icon={<RollbackOutlined />}
                onClick={() => {
                  setRejecting(subtask);
                  setRejectionReason("");
                }}
                type="primary"
              >
                退回修改
              </Button>
              {!canReview || actionIsBlocked ? (
                <span className="taskSubtaskManager__workflowDisabledHint">
                  {getActionHint("review")}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderSubtaskDetail = (subtask: TaskSubtaskItem | null) => {
    if (!subtask) {
      return (
        <div className="taskSubtaskManager__detailEmpty">
          <AuditOutlined />
          <strong>
            {subtasks.length ? "选择子任务查看详情" : "暂无子任务"}
          </strong>
          {subtasks.length > 0 && <span>再次点击可取消选择</span>}
        </div>
      );
    }

    const statusMeta = STATUS_META[subtask.status];
    const contentProgress = getContentProgress(subtask.content_items);
    const overdueItems = subtask.content_items.filter(isContentItemOverdue);
    const startTime = getSubtaskStartTime(subtask.content_items);
    const dueTime = getSubtaskDueTime(subtask.content_items);
    const priority = getSubtaskPriority(subtask.content_items);
    const subtaskAssignees = getTaskSubtaskContentAssignees(subtask);
    return (
      <div className="taskSubtaskManager__detail">
        <header className="taskSubtaskManager__detailHeader">
          <div>
            <small>子任务详情</small>
            <h4>{subtask.title}</h4>
          </div>
          <div className="taskSubtaskManager__detailBadges">
            <TaskPriorityTag priority={priority} />
            <Tag className={statusMeta.className}>{statusMeta.label}</Tag>
          </div>
        </header>
        <div className="taskSubtaskManager__detailSchedule">
          <CalendarOutlined />
          <span>
            <small>预计周期</small>
            <strong>
              {getSubtaskDateText(startTime)} 至{" "}
              {getSubtaskDateText(dueTime)}
            </strong>
          </span>
        </div>
        <div className="taskSubtaskManager__detailPeople">
          <span title={subtaskAssignees.map(getMemberName).join("、")}>
            <UserOutlined />
            <small>内容负责人</small>
            <strong>
              {subtaskAssignees.map(getMemberName).join("、")}
            </strong>
          </span>
          <span
            title={
              (subtask.transferees || []).length
                ? (subtask.transferees || []).map(getMemberName).join("、")
                : "无需转交"
            }
          >
            <SwapOutlined />
            <small>转让人</small>
            <strong>
              {(subtask.transferees || []).length
                ? (subtask.transferees || []).map(getMemberName).join("、")
                : "无需转交"}
            </strong>
          </span>
          <span
            title={
              subtask.reviewers.length
                ? subtask.reviewers.map(getMemberName).join("、")
                : "无需审核"
            }
          >
            <SafetyCertificateOutlined />
            <small>审核人</small>
            <strong>
              {subtask.reviewers.length
                ? subtask.reviewers.map(getMemberName).join("、")
                : "无需审核"}
            </strong>
          </span>
        </div>
        {subtask.status === "rejected" && subtask.rejection_reason && (
          <div className="taskSubtaskManager__detailRejection">
            <CloseCircleOutlined />
            <div>
              <small>驳回原因</small>
              <p>{subtask.rejection_reason}</p>
            </div>
          </div>
        )}
        <section
          className={`taskSubtaskManager__detailContent ${
            overdueItems.length ? "has-overdue-content" : ""
          }`}
        >
          <div className="taskSubtaskManager__detailContentHeader">
            <div>
              <strong>任务内容清单</strong>
              <span>
                {contentProgress.completed} / {contentProgress.total} 已完成
              </span>
            </div>
            <b>{contentProgress.progress}%</b>
          </div>
          {overdueItems.length ? (
            <div className="taskSubtaskManager__overdueNotice">
              <WarningOutlined />
              有 {overdueItems.length} 项内容逾期未完成
            </div>
          ) : null}
          <Progress
            percent={contentProgress.progress}
            showInfo={false}
            strokeColor="#22c55e"
            trailColor="#263448"
          />
          <div className="taskSubtaskManager__detailChecklist">
            {subtask.content_items.map((item, index) => (
              <article
                className={`${item.completed ? "is-completed" : ""} ${
                  isContentItemOverdue(item) ? "is-overdue" : ""
                } ${
                  item.item_id === focusedContentItemId
                    ? "is-focused-content"
                    : ""
                }`}
                data-content-item-id={item.item_id}
                key={item.item_id}
                ref={
                  item.item_id === focusedContentItemId
                    ? focusedContentItemRef
                    : undefined
                }
              >
                <button
                  aria-label={`切换内容项 ${index + 1} 完成状态`}
                  disabled={
                    workflowBusyId === subtask.id ||
                    (!canManageContentStatus &&
                      !item.assignees.includes(currentUserId))
                  }
                  onClick={() => togglePersistedContentItem(subtask, item.item_id)}
                  type="button"
                >
                  {item.completed ? <CheckCircleFilled /> : <span>{index + 1}</span>}
                </button>
                <div className="taskSubtaskManager__detailChecklistBody">
                  <div
                    className="taskSubtaskManager__detailChecklistContent"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                    onClick={onContentClick}
                  />
                  <div className="taskSubtaskManager__detailChecklistMeta">
                    <TaskPriorityTag priority={item.priority} />
                    <span>
                      <CalendarOutlined />
                      {getContentItemStartText(item)} 至{" "}
                      {getContentItemDueText(item)}
                    </span>
                    {isContentItemOverdue(item) ? (
                      <Tag className="is-overdue">
                        逾期 {getContentItemOverdueDays(item)} 天
                      </Tag>
                    ) : null}
                    <span>
                      <UserOutlined />
                      {item.assignees.map(getMemberName).join("、")}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
        <div className="taskSubtaskManager__detailWorkflow">
          {renderWorkflowActions(subtask)}
        </div>
      </div>
    );
  };

  const editor = editing ? (
    <div className="taskSubtaskManager__editor">
      <div className="taskSubtaskManager__editorTitle">
        <div>
          <span>{editing === "new" ? "新增" : "编辑"}</span>
          <strong>{editing === "new" ? "新增子任务" : "编辑子任务"}</strong>
        </div>
        {editing === "new" && (
          <Button
            aria-label="粘贴子任务"
            className="taskSubtaskManager__pasteButton"
            icon={<SnippetsOutlined />}
            onClick={pasteSubtask}
          >
            粘贴子任务
          </Button>
        )}
      </div>
      <label className="taskSubtaskManager__fieldLabel">子任务标题</label>
      <Input
        aria-label="子任务标题"
        maxLength={80}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="输入清晰、可验收的子任务标题"
        value={title}
      />
      <div className="taskSubtaskManager__peopleFields">
        <div>
          <label className="taskSubtaskManager__fieldLabel">转让人</label>
          <Select
            aria-label="子任务转让人"
            allowClear
            dropdownMatchSelectWidth={360}
            popupClassName="taskSubtaskMemberDropdown"
            listHeight={312}
            maxTagCount="responsive"
            mode="multiple"
            onChange={(value) => {
              setTransferees(value);
              setReviewers((current) =>
                current.filter((reviewer) => !value.includes(reviewer))
              );
            }}
            optionFilterProp="searchText"
            optionLabelProp="selectedLabel"
            options={transfereeSelectOptions}
            placeholder="可选；负责人完成后转交处理"
            showSearch
            value={transferees}
            virtual={false}
          />
        </div>
        <div>
          <label className="taskSubtaskManager__fieldLabel">审核人</label>
          <Select
            aria-label="子任务审核人"
            allowClear
            dropdownMatchSelectWidth={360}
            popupClassName="taskSubtaskMemberDropdown"
            listHeight={312}
            maxTagCount="responsive"
            mode="multiple"
            onChange={setReviewers}
            optionFilterProp="searchText"
            optionLabelProp="selectedLabel"
            options={reviewerSelectOptions}
            placeholder="可选；不设置时流程直接完成"
            showSearch
            value={reviewers}
            virtual={false}
          />
        </div>
      </div>
      <label className="taskSubtaskManager__fieldLabel">任务内容清单</label>
      <div className="taskSubtaskManager__contentEditorSummary">
        <span className="taskSubtaskManager__contentEditorIcon">
          <FileTextOutlined />
        </span>
        <div>
          <span>内容项进度</span>
          <strong>
            {getContentProgress(contentItems).completed} /{" "}
            {getContentProgress(contentItems).total} 已完成
          </strong>
        </div>
        <Progress
          percent={getContentProgress(contentItems).progress}
          showInfo={false}
          strokeColor="#22c55e"
          trailColor="#263448"
        />
        <Button
          icon={<EditOutlined />}
          onClick={() => setContentChecklistOpen(true)}
          type="primary"
        >
          {contentItems.length ? "管理任务内容" : "新增任务内容"}
        </Button>
        {contentItems.length ? (
          <div
            aria-label="任务内容清单预览"
            className="taskSubtaskManager__contentEditorList"
            role="list"
          >
            {contentItems.map((item, index) => {
              const overdue = isContentItemOverdue(item);
              return (
                <article
                  className={`${item.completed ? "is-completed" : ""} ${
                    overdue ? "is-overdue" : ""
                  }`}
                  key={item.item_id}
                  role="listitem"
                >
                  <span className="taskSubtaskManager__contentEditorState">
                    {item.completed ? <CheckCircleFilled /> : index + 1}
                  </span>
                  <div className="taskSubtaskManager__contentEditorItemBody">
                    <div
                      className="taskSubtaskManager__contentEditorItemText"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                      onClick={onContentClick}
                    />
                    <div className="taskSubtaskManager__contentEditorItemMeta">
                      <Tag className={item.completed ? "is-done" : "is-pending"}>
                        {item.completed ? "已完成" : "未完成"}
                      </Tag>
                      <TaskPriorityTag priority={item.priority} />
                      <span>
                        <CalendarOutlined />
                        {getContentItemStartText(item)} 至 {getContentItemDueText(item)}
                      </span>
                      <span>
                        <UserOutlined />
                        {item.assignees.map(getMemberName).join("、")}
                      </span>
                      {overdue ? (
                        <Tag className="is-overdue">
                          逾期 {getContentItemOverdueDays(item)} 天
                        </Tag>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="taskSubtaskManager__contentEditorEmpty">
            暂无任务内容，点击右侧按钮添加
          </div>
        )}
      </div>
      <div className="taskSubtaskManager__editorFooter">
        <span>
          {transferees.length
            ? reviewers.length
              ? "全部内容完成后自动转交，由转让人提交给审核人"
              : "全部内容完成后自动转交，由转让人确认完成"
            : reviewers.length
            ? "全部内容完成后自动提交，由任一审核人审批"
            : "未设置转让人和审核人，全部内容完成后自动完成"}
        </span>
        <div>
          <Button disabled={saving} onClick={resetEditor}>取消</Button>
          <Button loading={saving} onClick={save} type="primary">
            保存子任务
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="taskSubtaskManager__editorEmpty">
      <EditOutlined />
      <strong>选择子任务进行编辑</strong>
      <span>{subtasks.length} 个子任务</span>
    </div>
  );

  return (
    <section className={`taskSubtaskManager ${canManage ? "" : "is-readonly"}`}>
      <header className="taskSubtaskManager__header">
        <div>
          <h3>子任务拆分</h3>
          <span>{subtasks.length ? `${completedCount} / ${subtasks.length} 已完成` : "未拆分"}</span>
        </div>
        <strong>{progress}%</strong>
      </header>

      <div className="taskSubtaskManager__progress">
        <Progress percent={progress} showInfo={false} strokeColor="#22c55e" trailColor="#263448" />
      </div>

      <div
        className={`taskSubtaskManager__workspace ${
          detailMode ? "is-detail-mode" : ""
        }`}
      >
        <section aria-label="子任务列表" className="taskSubtaskManager__listPane">
          <div className="taskSubtaskManager__listToolbar">
            <strong>{detailMode ? "子任务摘要" : "任务列表"}</strong>
            {canManage && (
              <Button
                aria-label="新增子任务"
                disabled={Boolean(editing)}
                icon={<PlusOutlined />}
                onClick={beginCreate}
                type="primary"
              >
                新增
              </Button>
            )}
          </div>
          <Spin spinning={loading}>
            <div className="taskSubtaskManager__list">
              {orderedSubtasks.map((subtask, index) => {
                const statusMeta = STATUS_META[subtask.status];
                const isBusy = workflowBusyId === subtask.id;
                const contentProgress = getContentProgress(subtask.content_items);
                const overdueItems = subtask.content_items.filter(isContentItemOverdue);
                const startTime = getSubtaskStartTime(subtask.content_items);
                const dueTime = getSubtaskDueTime(subtask.content_items);
                const priority = getSubtaskPriority(subtask.content_items);
                const nextDueItem = subtask.content_items
                  .filter((item) => !item.completed)
                  .sort((left, right) => left.due_time - right.due_time)[0];
                return (
                  <article
                    aria-label={
                      detailMode
                        ? `查看子任务详情 ${subtask.title}`
                        : undefined
                    }
                    aria-pressed={
                      detailMode ? selectedSubtask?.id === subtask.id : undefined
                    }
                    className={`taskSubtaskManager__item ${statusMeta.className} ${
                      (detailMode && selectedSubtask?.id === subtask.id) ||
                      (editing !== "new" && editing?.id === subtask.id)
                        ? "is-active"
                        : ""
                    } ${subtask.id === focusedSubtaskId ? "is-focused" : ""} ${
                      overdueItems.length ? "has-overdue-content" : ""
                    } ${
                      !detailMode && canManage && !saving && !isBusy
                        ? "is-click-editable"
                        : ""
                    }`}
                    data-subtask-id={subtask.id}
                    key={subtask.id}
                    onClick={(event) =>
                      handleSubtaskCardClick(event, subtask, isBusy)
                    }
                    onKeyDown={(event) => {
                      if (
                        detailMode &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        selectSubtask(subtask);
                      }
                    }}
                    ref={(node) => {
                      if (subtask.id === focusedSubtaskId) {
                        focusedSubtaskRef.current = node;
                      }
                    }}
                    role={detailMode ? "button" : undefined}
                    tabIndex={detailMode ? 0 : undefined}
                  >
                    <div className="taskSubtaskManager__index">
                      {subtask.status === "done" ? <CheckCircleFilled /> : index + 1}
                    </div>
                    <div className="taskSubtaskManager__itemBody">
                      <div className="taskSubtaskManager__itemHeading">
                        <strong title={subtask.title}>{subtask.title}</strong>
                        <TaskPriorityTag priority={priority} />
                        <Tag className={statusMeta.className}>{statusMeta.label}</Tag>
                        {overdueItems.length ? (
                          <Tag className="is-overdue-content">
                            <WarningOutlined /> {overdueItems.length} 项逾期
                          </Tag>
                        ) : null}
                      </div>
                      <div className="taskSubtaskManager__itemSchedule">
                        <CalendarOutlined />
                        <span>
                          {getSubtaskDateText(startTime)} 至{" "}
                          {getSubtaskDateText(dueTime)}
                        </span>
                      </div>
                      <div className="taskSubtaskManager__itemPeople">
                        <span
                          title={getTaskSubtaskContentAssignees(subtask)
                            .map(getMemberName)
                            .join("、")}
                        >
                          <UserOutlined />内容负责人 ·{" "}
                          {getTaskSubtaskContentAssignees(subtask)
                            .map(getMemberName)
                            .join("、")}
                        </span>
                        {(subtask.transferees || []).length ? (
                          <span
                            title={(subtask.transferees || [])
                              .map(getMemberName)
                              .join("、")}
                          >
                            <SwapOutlined />转让人 ·{" "}
                            {(subtask.transferees || [])
                              .map(getMemberName)
                              .join("、")}
                          </span>
                        ) : null}
                        <span
                          title={
                            subtask.reviewers.length
                              ? subtask.reviewers
                                  .map(getMemberName)
                                  .join("、")
                              : "无需审核"
                          }
                        >
                          <SafetyCertificateOutlined />审核人 ·{" "}
                          {subtask.reviewers.length
                            ? subtask.reviewers.map(getMemberName).join("、")
                            : "无需审核"}
                        </span>
                      </div>
                      {nextDueItem ? (
                        <div
                          className={`taskSubtaskManager__itemDue ${
                            isContentItemOverdue(nextDueItem) ? "is-overdue" : ""
                          }`}
                        >
                          <CalendarOutlined />
                          {isContentItemOverdue(nextDueItem)
                            ? `最近内容已逾期 ${getContentItemOverdueDays(nextDueItem)} 天`
                            : `最近预期完成：${getContentItemDueText(nextDueItem)}`}
                        </div>
                      ) : null}
                      <div className="taskSubtaskManager__itemContentProgress">
                        <span>
                          内容 {contentProgress.completed} / {contentProgress.total}
                        </span>
                        <Progress
                          percent={contentProgress.progress}
                          showInfo={false}
                          strokeColor="#22c55e"
                          trailColor="#263448"
                        />
                      </div>
                      {!detailMode && (
                        <div className="taskSubtaskManager__contentChecklistPreview">
                          {subtask.content_items.map((item) => (
                            <div
                              className={`${item.completed ? "is-completed" : ""} ${
                                isContentItemOverdue(item) ? "is-overdue" : ""
                              }`}
                              key={item.item_id}
                              title={`${getContentItemLabel(
                                item.content
                              )} · ${getContentItemStartText(
                                item
                              )} 至 ${getContentItemDueText(item)}`}
                            >
                              <span className="taskSubtaskManager__contentChecklistPreviewTitle">
                                {item.completed ? (
                                  <CheckCircleFilled />
                                ) : isContentItemOverdue(item) ? (
                                  <WarningOutlined />
                                ) : (
                                  <i />
                                )}
                                <strong>{getContentItemLabel(item.content)}</strong>
                              </span>
                              <span className="taskSubtaskManager__contentChecklistPreviewMeta">
                                <TaskPriorityTag priority={item.priority} />
                                <span>
                                  <CalendarOutlined />
                                  {getContentItemStartText(item)} 至{" "}
                                  {getContentItemDueText(item)}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {subtask.status === "rejected" && subtask.rejection_reason && (
                        <div className="taskSubtaskManager__rejectionReason">
                          <CloseCircleOutlined />
                          <span>驳回原因：{subtask.rejection_reason}</span>
                        </div>
                      )}
                      {!detailMode && renderWorkflowActions(subtask)}
                    </div>
                    <div className="taskSubtaskManager__actions">
                      <Tooltip title="复制子任务">
                        <Button
                          aria-label={`复制子任务 ${subtask.title}`}
                          icon={<CopyOutlined />}
                          onClick={() => copySubtask(subtask)}
                        />
                      </Tooltip>
                      {canManage && (
                        <>
                        <Tooltip title="编辑">
                          <Button
                            aria-label="编辑子任务"
                            disabled={saving || isBusy}
                            icon={<EditOutlined />}
                            onClick={() => switchEditorToSubtask(subtask)}
                          />
                        </Tooltip>
                        <Tooltip title="删除">
                          <Button
                            aria-label="删除子任务"
                            danger
                            disabled={Boolean(editing) || isBusy}
                            icon={<DeleteOutlined />}
                            onClick={() => setDeleting(subtask)}
                          />
                        </Tooltip>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
              {!loading && !orderedSubtasks.length && (
                <div className="taskSubtaskManager__empty">
                  <AuditOutlined />
                  <strong>暂未拆分子任务</strong>
                  <span>0 个子任务 · 进度 0%</span>
                </div>
              )}
            </div>
          </Spin>
        </section>

        {detailMode && (
          <section
            aria-label="子任务详情"
            className="taskSubtaskManager__detailPane"
          >
            {renderSubtaskDetail(selectedSubtask)}
          </section>
        )}

        {!detailMode && canManage && (
          <section aria-label="子任务编辑区" className="taskSubtaskManager__editorPane">
            {editor}
          </section>
        )}
      </div>

      <Modal
        maskClosable={false}
        keyboard={false}
        centered
        className="taskSubtaskCompactDetailModal"
        destroyOnClose
        footer={null}
        onCancel={() => setDetailModalOpen(false)}
        open={detailMode && detailModalOpen}
        title="子任务详情"
        width={680}
      >
        {renderSubtaskDetail(selectedSubtask)}
      </Modal>

      <SubtaskContentChecklistModal
        canManageContentStatus={canManageContentStatus}
        currentUserId={currentUserId}
        defaultAssignee={defaultAssignee}
        getMemberName={getMemberName}
        items={contentItems}
        memberOptions={memberOptions}
        onChange={setContentItems}
        onClose={() => setContentChecklistOpen(false)}
        open={contentChecklistOpen}
      />

      <Modal
        maskClosable={false}
        keyboard={false}
        centered
        className="taskSubtaskDeleteModal"
        confirmLoading={saving}
        okButtonProps={{ danger: true }}
        okText="确认删除"
        onCancel={() => !saving && setDeleting(null)}
        onOk={async () => {
          if (!deleting) return;
          setSaving(true);
          try {
            if (await onDelete(deleting)) setDeleting(null);
          } finally {
            setSaving(false);
          }
        }}
        open={Boolean(deleting)}
        title="删除子任务"
        cancelText="取消"
      >
        <p>删除后将重新计算主任务进度与状态，该操作会记录在项目日志中。</p>
      </Modal>

      <Modal
        maskClosable={false}
        keyboard={false}
        centered
        className="taskSubtaskReviewModal"
        destroyOnClose
        footer={
          <div className="taskSubtaskReviewModal__footer">
            <Button disabled={Boolean(workflowBusyId)} onClick={closeRejectionModal}>
              继续审核
            </Button>
            <Button
              aria-label="确认退回"
              className="taskSubtaskReviewModal__confirm"
              danger
              disabled={!rejectionReason.trim()}
              icon={<RollbackOutlined />}
              loading={Boolean(rejecting && workflowBusyId === rejecting.id)}
              onClick={confirmRejection}
              type="primary"
            >
              确认退回
            </Button>
          </div>
        }
        onCancel={closeRejectionModal}
        open={Boolean(rejecting)}
        title={
          <div className="taskSubtaskReviewModal__title">
            <span><ExclamationCircleOutlined /></span>
            <div>
              <small>REVIEW DECISION</small>
              <strong>驳回子任务</strong>
            </div>
          </div>
        }
        width={540}
      >
        <div className="taskSubtaskReviewModal__intro">
          <span><RollbackOutlined /></span>
          <div>
            <strong>退回给负责人修改</strong>
            <p>子任务将变为“已驳回”，负责人修改后可以再次提交审批。</p>
          </div>
        </div>
        {rejecting && (
          <div className="taskSubtaskReviewModal__target">
            <span>当前子任务</span>
            <strong title={rejecting.title}>{rejecting.title}</strong>
            <small>
              内容负责人 · {getTaskSubtaskContentAssignees(rejecting)
                .map(getMemberName)
                .join("、")}
            </small>
          </div>
        )}
        <div className="taskSubtaskReviewModal__notice">
          <AuditOutlined />
          <span>负责人将收到站内消息和钉钉通知</span>
        </div>
        <label className="taskSubtaskReviewModal__fieldLabel">
          <span>驳回原因</span>
          <em>必填</em>
        </label>
        <Input.TextArea
          aria-label="子任务驳回原因"
          maxLength={300}
          onChange={(event) => setRejectionReason(event.target.value)}
          placeholder="填写明确、可执行的修改意见"
          rows={5}
          showCount
          value={rejectionReason}
        />
      </Modal>
    </section>
  );
};

export default TaskSubtaskManager;
